import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Progress,
  Stack,
  Text,
  Textarea,
  VStack,
  Spinner,
  Badge,
  Divider,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react'
import { CheckCircle2, AlertCircle, Clock, MessageSquare } from 'lucide-react'
import { getCsatSurveyByToken, submitCsatSurvey, type CsatSurveyPayload } from '@/lib/services/csatService'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

function CsatPageShell({ children }: { children: ReactNode }) {
  const colors = useThemeColors()
  const { settings } = useSiteSettings()
  const heroGradient = useColorModeValue(
    'linear(to-b, blue.50, gray.50 45%)',
    'linear(to-b, blue.900, gray.900 45%)'
  )

  return (
    <Box minH="100vh" bg={colors.bg} bgGradient={heroGradient}>
      <Container maxW="lg" py={{ base: 8, md: 12 }} px={{ base: 4, md: 6 }}>
        <VStack align="stretch" spacing={6}>
          <VStack spacing={3} textAlign="center" pt={2}>
            <Box as="img" src={settings.auth_logo || '/assets/img/als-logo-2.png'} alt="ActionLabs" maxW="220px" />
            <HStack spacing={2} color={colors.textSecondary} fontSize="sm" fontWeight="600">
              <Icon as={MessageSquare} boxSize={4} />
              <Text>Customer satisfaction survey</Text>
            </HStack>
          </VStack>
          {children}
        </VStack>
      </Container>
    </Box>
  )
}

function StatusCard({
  tone,
  title,
  description,
  icon,
}: {
  tone: 'success' | 'error' | 'warning'
  title: string
  description: string
  icon: typeof CheckCircle2
}) {
  const colors = useThemeColors()
  const toneMap = {
    success: { border: 'green.200', bg: 'green.50', icon: 'green.500', title: 'green.800' },
    error: { border: 'red.200', bg: 'red.50', icon: 'red.500', title: 'red.800' },
    warning: { border: 'orange.200', bg: 'orange.50', icon: 'orange.500', title: 'orange.800' },
  } as const
  const t = toneMap[tone]

  return (
    <Box
      bg={colors.cardBg}
      border="1px solid"
      borderColor={t.border}
      borderRadius="2xl"
      p={{ base: 6, md: 8 }}
      shadow="md"
      textAlign="center"
    >
      <VStack spacing={4}>
        <Box bg={t.bg} borderRadius="full" p={3}>
          <Icon as={icon} boxSize={8} color={t.icon} />
        </Box>
        <Heading size="md" color={t.title}>
          {title}
        </Heading>
        <Text color={colors.text} fontSize="md" maxW="sm">
          {description}
        </Text>
      </VStack>
    </Box>
  )
}

function RatingScale({
  value,
  onChange,
  isDisabled,
}: {
  value: string
  onChange: (val: string) => void
  isDisabled?: boolean
}) {
  const colors = useThemeColors()
  const labels = ['Very dissatisfied', 'Very satisfied']

  return (
    <VStack align="stretch" spacing={3} pt={1}>
      <HStack spacing={2} justify="space-between">
        {[1, 2, 3, 4, 5].map((n) => {
          const selected = value === String(n)
          return (
            <Button
              key={n}
              flex={1}
              h={{ base: '44px', md: '48px' }}
              minW={0}
              px={0}
              fontSize="lg"
              fontWeight="700"
              variant={selected ? 'solid' : 'outline'}
              colorScheme={selected ? 'blue' : 'gray'}
              borderColor={selected ? 'blue.500' : colors.border}
              bg={selected ? undefined : colors.inputBg}
              aria-label={`Rate ${n} out of 5`}
              aria-pressed={selected}
              isDisabled={isDisabled}
              onClick={() => onChange(String(n))}
              _hover={selected ? undefined : { borderColor: 'blue.300', bg: 'blue.50' }}
            >
              {n}
            </Button>
          )
        })}
      </HStack>
      <HStack justify="space-between" fontSize="xs" color={colors.textMuted} px={0.5}>
        <Text>{labels[0]}</Text>
        <Text>{labels[1]}</Text>
      </HStack>
    </VStack>
  )
}

export default function CsatSurveyPage() {
  const { token = '' } = useParams<{ token: string }>()
  const colors = useThemeColors()
  const [survey, setSurvey] = useState<CsatSurveyPayload | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [answers, setAnswers] = useState<Record<number, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!token) {
      setError('Invalid survey link.')
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    getCsatSurveyByToken(token)
      .then((data) => {
        if (cancelled) return
        setSurvey(data)
        if (data.responded) setSubmitted(true)
        if (data.expired && !data.responded) setError('This survey invitation has expired.')
      })
      .catch(() => {
        if (cancelled) return
        setError('Survey not found or no longer available.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [token])

  const requiredQuestions = useMemo(
    () => survey?.questions.filter((q) => q.required) ?? [],
    [survey]
  )

  const answeredRequiredCount = useMemo(
    () =>
      requiredQuestions.filter((q) => q.id != null && (answers[q.id] ?? '').trim() !== '').length,
    [requiredQuestions, answers]
  )

  const progressPercent =
    requiredQuestions.length > 0
      ? Math.round((answeredRequiredCount / requiredQuestions.length) * 100)
      : 100

  const canSubmit =
    survey &&
    !survey.expired &&
    !isSubmitting &&
    (requiredQuestions.length === 0 || answeredRequiredCount === requiredQuestions.length)

  const handleSubmit = async () => {
    if (!survey || !token) return

    const payload = survey.questions.map((q) => ({
      question_id: q.id!,
      value: answers[q.id!] ?? '',
    }))

    const missingRequired = survey.questions.some(
      (q) => q.required && !(answers[q.id!] ?? '').trim()
    )
    if (missingRequired) {
      setError('Please answer all required questions before submitting.')
      return
    }

    setIsSubmitting(true)
    setError(null)
    try {
      await submitCsatSurvey(token, payload)
      setSubmitted(true)
    } catch {
      setError('Unable to submit your response. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <CsatPageShell>
        <Box
          bg={colors.cardBg}
          borderRadius="2xl"
          p={10}
          shadow="md"
          border="1px solid"
          borderColor={colors.borderLight}
        >
          <VStack spacing={4}>
            <Spinner size="lg" color="blue.500" thickness="3px" />
            <Text color={colors.text} fontWeight="500">
              Loading your survey…
            </Text>
          </VStack>
        </Box>
      </CsatPageShell>
    )
  }

  if (submitted) {
    return (
      <CsatPageShell>
        <StatusCard
          tone="success"
          icon={CheckCircle2}
          title="Thank you for your feedback"
          description="Your response has been recorded. We appreciate you taking the time to help us improve our support experience."
        />
      </CsatPageShell>
    )
  }

  if (error && !survey) {
    return (
      <CsatPageShell>
        <StatusCard
          tone="error"
          icon={AlertCircle}
          title="Survey unavailable"
          description={error}
        />
      </CsatPageShell>
    )
  }

  if (!survey) return null

  if (survey.expired) {
    return (
      <CsatPageShell>
        <StatusCard
          tone="warning"
          icon={Clock}
          title="Survey expired"
          description={error ?? 'This survey invitation has expired. If you still want to share feedback, please contact your support team.'}
        />
      </CsatPageShell>
    )
  }

  return (
    <CsatPageShell>
      <Box
        bg={colors.cardBg}
        borderRadius="2xl"
        p={{ base: 5, md: 7 }}
        shadow="md"
        border="1px solid"
        borderColor={colors.borderLight}
      >
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="lg" color={colors.heading} mb={2}>
              {survey.survey_name}
            </Heading>
            {survey.survey_description ? (
              <Text color={colors.text} fontSize="md" mb={3}>
                {survey.survey_description}
              </Text>
            ) : (
              <Text color={colors.text} fontSize="md" mb={3}>
                Tell us how we did on your recent support request. This only takes a minute.
              </Text>
            )}
            {survey.ticket_number ? (
              <Badge colorScheme="blue" variant="subtle" fontSize="sm" px={2} py={1} borderRadius="md">
                Ticket {survey.ticket_number}
              </Badge>
            ) : null}
          </Box>

          {requiredQuestions.length > 0 ? (
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" fontWeight="600" color={colors.textSecondary}>
                  Progress
                </Text>
                <Text fontSize="sm" color={colors.textMuted}>
                  {answeredRequiredCount} of {requiredQuestions.length} required
                </Text>
              </HStack>
              <Progress
                value={progressPercent}
                size="sm"
                borderRadius="full"
                colorScheme={progressPercent === 100 ? 'green' : 'blue'}
                bg={colors.progressBg}
              />
            </Box>
          ) : null}

          <Divider borderColor={colors.borderLight} />

          {error ? (
            <Box
              bg="orange.50"
              border="1px solid"
              borderColor="orange.200"
              borderRadius="lg"
              px={4}
              py={3}
              fontSize="sm"
              color="orange.800"
            >
              {error}
            </Box>
          ) : null}

          <Stack spacing={4}>
            {survey.questions.map((question, index) => (
              <Box
                key={question.id}
                border="1px solid"
                borderColor={colors.borderLight}
                borderRadius="xl"
                p={{ base: 4, md: 5 }}
                bg={colors.sectionBg}
              >
                <FormControl isRequired={question.required}>
                  <FormLabel mb={3} fontWeight="600" color={colors.heading}>
                    <HStack align="start" spacing={3}>
                      <Badge
                        colorScheme="blue"
                        variant="solid"
                        borderRadius="full"
                        minW="28px"
                        textAlign="center"
                      >
                        {index + 1}
                      </Badge>
                      <Text as="span" flex={1} pt={0.5}>
                        {question.question_text}
                        {!question.required ? (
                          <Text as="span" color={colors.textMuted} fontWeight="normal" ml={1}>
                            (optional)
                          </Text>
                        ) : null}
                      </Text>
                    </HStack>
                  </FormLabel>
                  {question.question_type === 'rating' ? (
                    <RatingScale
                      value={answers[question.id!] ?? ''}
                      onChange={(val) => setAnswers((prev) => ({ ...prev, [question.id!]: val }))}
                    />
                  ) : (
                    <Textarea
                      value={answers[question.id!] ?? ''}
                      onChange={(e) =>
                        setAnswers((prev) => ({ ...prev, [question.id!]: e.target.value }))
                      }
                      rows={4}
                      placeholder="Share any additional comments…"
                      bg={colors.inputBg}
                      borderColor={colors.inputBorder}
                      _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
                    />
                  )}
                </FormControl>
              </Box>
            ))}
          </Stack>

          <Button
            colorScheme="blue"
            size="lg"
            w="full"
            h="52px"
            fontSize="md"
            onClick={() => void handleSubmit()}
            isLoading={isSubmitting}
            loadingText="Submitting…"
            isDisabled={!canSubmit}
          >
            Submit feedback
          </Button>

          <Text fontSize="xs" color={colors.textMuted} textAlign="center">
            Your responses are used to improve our service. Required questions must be completed to submit.
          </Text>
        </VStack>
      </Box>
    </CsatPageShell>
  )
}
